// Dialogs for the Project Datasheet Composer's Index Sheet Datasheets tab. STUI popup skeleton
// throughout (ConfirmModal from the master Catalog tab supplies it).
import { useState } from 'react';
import { ConfirmModal } from './CatalogModals';
import { templateLabel, revOf } from './projectSheets';
import StuiSelect from '../../components/common/StuiSelect';

/**
 * Create a sheet's datasheet from a Published ISA Template. When the library already holds a copy
 * of the picked form for this code (another project made it), the server answers 409 with that
 * copy; `existing` then carries it and the dialog offers to use it instead of creating a second one.
 */
export function CreateDatasheetModal({ row, templates = [], existing = null, busy = false, onCreate, onUseExisting, onClose }) {
  const [templateId, setTemplateId] = useState(templates[0]?.id ?? '');
  const picked = templates.find(t => String(t.id) === String(templateId));

  if (existing) {
    return (
      <ConfirmModal title={`Datasheet for ${row.indexSheet}`} confirmLabel="Use the existing datasheet" busy={busy}
        onConfirm={() => onUseExisting(existing.existingId)} onClose={onClose}>
        <p className="stui-hint">{existing.error}</p>
        <p className="stui-hint">
          {`${row.indexSheet} is pointed at ${existing.existingDsIndex}` +
            (existing.existingInProject ? '.' : ', which is first copied into this project with the same IDs.')}
        </p>
      </ConfirmModal>
    );
  }

  return (
    <ConfirmModal title={`Create datasheet for ${row.indexSheet}`} confirmLabel="Create" busy={busy} confirmDisabled={!picked}
      onConfirm={() => onCreate(picked.id)} onClose={onClose}>
      {templates.length === 0 ? (
        <p className="stui-hint">
          There are no Published ISA Templates yet. A template is published from the Master Datasheet Composer.
        </p>
      ) : (
        <>
          <div className="stui-field-group stui-mb-md">
            <label htmlFor="ds-create-template">ISA Template</label>
            <StuiSelect id="ds-create-template" className="stui-select" value={templateId} disabled={busy}
              onChange={e => setTemplateId(e.target.value)}>
              {templates.map(t => <option key={t.id} value={t.id}>{templateLabel(t)}</option>)}
            </StuiSelect>
          </div>
          {picked && (
            <p className="stui-hint">
              {`Creates ${picked.dsName}_${row.indexSheet}_00 from ${picked.dsIndex} in the template library and in this ` +
                `project (same IDs), and assigns it to ${row.indexSheet}.`}
            </p>
          )}
        </>
      )}
    </ConfirmModal>
  );
}

/**
 * Point a sheet at another datasheet: a revision of its own, or for a sheet with none, a copy
 * carrying its code that another project made.
 */
export function AssignDatasheetModal({ row, busy = false, onAssign, onClose }) {
  const candidates = row.candidates ?? [];
  const [dsIndexId, setDsIndexId] = useState(candidates[0]?.id ?? '');
  const picked = candidates.find(c => String(c.id) === String(dsIndexId));

  return (
    <ConfirmModal title={row.dsConfig == null ? `Use an existing datasheet for ${row.indexSheet}` : `Assign another revision to ${row.indexSheet}`}
      confirmLabel="Assign" busy={busy} confirmDisabled={!picked} onConfirm={() => onAssign(picked.id)} onClose={onClose}>
      <div className="stui-field-group stui-mb-md">
        <label htmlFor="ds-assign-target">Datasheet</label>
        <StuiSelect id="ds-assign-target" className="stui-select" value={dsIndexId} disabled={busy}
          onChange={e => setDsIndexId(e.target.value)}>
          {candidates.map(c => (
            <option key={c.id} value={c.id}>{`${c.dsIndex}${c.inProject ? '' : ' (not in this project yet)'}`}</option>
          ))}
        </StuiSelect>
      </div>
      {picked && !picked.inProject && (
        <p className="stui-hint">{`${picked.dsIndex} is first copied into this project from the template library, with the same IDs.`}</p>
      )}
      {row.dsConfig != null && row.valueCount > 0 && (
        <p className="stui-hint">
          {`The ${row.valueCount} value${row.valueCount === 1 ? '' : 's'} on rev ${revOf(row.dsIndex)} do not move: they stay stored with ` +
            `that revision and show again if ${row.indexSheet} is pointed back at it.`}
        </p>
      )}
    </ConfirmModal>
  );
}

/** Make the next revision of a sheet's datasheet, project first. */
export function ReviseModal({ row, busy = false, onRevise, onClose }) {
  return (
    <ConfirmModal title={`Revise ${row.dsIndex}`} confirmLabel="Create revision" busy={busy} onConfirm={onRevise} onClose={onClose}>
      <p className="stui-hint">
        {`Duplicates ${row.dsIndex} to the next revision in the template library and in this project (same IDs). ` +
          `Only ${row.indexSheet} in this database moves to the new revision, and its ${row.valueCount} value${row.valueCount === 1 ? '' : 's'} move onto the new boxes.`}
      </p>
      <p className="stui-hint">
        {`Rev ${revOf(row.dsIndex)} stays as history. Other projects using it are not changed.`}
      </p>
    </ConfirmModal>
  );
}
